import React, { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Search, RefreshCw, Download, Edit2, Lock, Unlock,
  Trash2, ShieldCheck, Award, TrendingUp, Phone, X, CheckCircle, Eye, ArrowLeft,
  ListOrdered, FileText, Calendar, BookOpen, ChevronRight, Check,
  ArrowUp, ArrowDown, ChevronsUp, ChevronsDown, Save, Sparkles, AlertCircle,
  Building2, DollarSign, Layers
} from "lucide-react";
import { VerifiedPredictor, generatePdfHtml } from "./GuestPredictorGate";

export default function PublicAdminGuestStudents() {
  const [adminTab, setAdminTab] = useState("students"); // "students" | "preferences"

  // Students Registry State
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [lockFilter, setLockFilter] = useState("ALL");
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Saved Preferences State
  const [preferencesList, setPreferencesList] = useState([]);
  const [prefLoading, setPrefLoading] = useState(false);
  const [prefSearch, setPrefSearch] = useState("");
  const [prefCourseFilter, setPrefCourseFilter] = useState("ALL");

  // Admin Full-Screen Workspace State
  const [viewingPref, setViewingPref] = useState(null);
  const [modalPrefs, setModalPrefs] = useState([]);
  const [savingModalPrefs, setSavingModalPrefs] = useState(false);
  const [workspaceSearch, setWorkspaceSearch] = useState("");
  const [workspaceTypeFilter, setWorkspaceTypeFilter] = useState("ALL");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Edit Student Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editForm, setEditForm] = useState({
    reg_no: "",
    sml: "",
    category: "OPEN",
    category_rank: "",
    gender: "Male",
    is_locked: true,
  });
  const [saving, setSaving] = useState(false);
  const [unlockingAll, setUnlockingAll] = useState(false);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const res = await api.get("/medical-predictor/admin/guest-students");
      if (res.data?.students) {
        setStudents(res.data.students);
      }
    } catch (err) {
      toast.error("Failed to load locked guest students.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    setPrefLoading(true);
    try {
      const res = await api.get("/medical-predictor/admin/all-preferences");
      if (res.data?.preferences) {
        setPreferencesList(res.data.preferences);
      }
    } catch (err) {
      toast.error("Failed to load student priority lists.");
    } finally {
      setPrefLoading(false);
    }
  };

  // Open Full-Screen Workspace for a student's preference list
  const handleOpenViewingPref = (pref) => {
    setViewingPref(pref);
    setModalPrefs(pref.preferences || []);
    setWorkspaceSearch("");
    setWorkspaceTypeFilter("ALL");
    setHasUnsavedChanges(false);
  };

  // Admin Reordering Functions
  const moveModalPrefUp = (actualIdx) => {
    if (actualIdx === 0) return;
    const updated = [...modalPrefs];
    [updated[actualIdx - 1], updated[actualIdx]] = [updated[actualIdx], updated[actualIdx - 1]];
    setModalPrefs(updated.map((p, i) => ({ ...p, pref_order: i + 1 })));
    setHasUnsavedChanges(true);
  };

  const moveModalPrefDown = (actualIdx) => {
    if (actualIdx === modalPrefs.length - 1) return;
    const updated = [...modalPrefs];
    [updated[actualIdx], updated[actualIdx + 1]] = [updated[actualIdx + 1], updated[actualIdx]];
    setModalPrefs(updated.map((p, i) => ({ ...p, pref_order: i + 1 })));
    setHasUnsavedChanges(true);
  };

  const moveModalPrefToTop = (actualIdx) => {
    if (actualIdx === 0) return;
    const item = modalPrefs[actualIdx];
    const updated = [item, ...modalPrefs.filter((_, i) => i !== actualIdx)];
    setModalPrefs(updated.map((p, i) => ({ ...p, pref_order: i + 1 })));
    setHasUnsavedChanges(true);
  };

  const moveModalPrefToBottom = (actualIdx) => {
    if (actualIdx === modalPrefs.length - 1) return;
    const item = modalPrefs[actualIdx];
    const updated = [...modalPrefs.filter((_, i) => i !== actualIdx), item];
    setModalPrefs(updated.map((p, i) => ({ ...p, pref_order: i + 1 })));
    setHasUnsavedChanges(true);
  };

  const removeModalPref = (actualIdx) => {
    const updated = modalPrefs.filter((_, i) => i !== actualIdx).map((p, i) => ({ ...p, pref_order: i + 1 }));
    setModalPrefs(updated);
    setHasUnsavedChanges(true);
    toast.info("Removed choice from list");
  };

  // Admin Save Updated Preferences to Cloud
  const handleSaveModalPrefs = async () => {
    if (!viewingPref) return;
    setSavingModalPrefs(true);
    try {
      const prefs = modalPrefs.map((p, i) => ({ ...p, pref_order: i + 1 }));
      await api.post("/medical-predictor/preferences/save", {
        reg_no: viewingPref.reg_no,
        course: viewingPref.course || "MBBS",
        preferences: prefs,
        student_name: viewingPref.student_name,
      });
      toast.success(`Saved updated priority list (${prefs.length} choices) for ${viewingPref.student_name}`);
      setHasUnsavedChanges(false);
      fetchPreferences();
      setViewingPref(prev => ({ ...prev, preferences: prefs, total_choices: prefs.length, saved_at: new Date().toISOString() }));
    } catch {
      toast.error("Failed to save updated priority list.");
    } finally {
      setSavingModalPrefs(false);
    }
  };

  // Admin Download PDF for Selected Student
  const handleAdminDownloadPdf = () => {
    if (!viewingPref || !modalPrefs.length) {
      toast.error("No priority choices to export.");
      return;
    }
    toast.info("Generating Priority List PDF…");
    const matchingStudent = students.find(s => s.reg_no === viewingPref.reg_no) || {
      candidate_name: viewingPref.student_name || "Student",
      reg_no: viewingPref.reg_no,
      air: viewingPref.air || null,
      sml: viewingPref.sml || null,
      category: viewingPref.category || "OPEN",
      category_rank: viewingPref.category_rank || null,
      gender: viewingPref.gender || "Male",
    };

    const html = generatePdfHtml(matchingStudent, modalPrefs, viewingPref.course || "MBBS", modalPrefs);
    const el = document.createElement("div");
    el.innerHTML = html;
    import("html2pdf.js").then(({ default: html2pdf }) => {
      html2pdf().set({
        margin: [6, 6, 8, 6],
        filename: `PriorityList_${viewingPref.reg_no}_${viewingPref.course || "MBBS"}.pdf`,
        image: { type: "jpeg", quality: 0.88 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape", compress: true },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] }
      }).from(el).save().then(() => toast.success("Priority List PDF downloaded!"));
    }).catch(() => toast.error("PDF generation failed."));
  };

  // Bulk Unlock All Students
  const handleUnlockAll = async () => {
    const lockedCount = students.filter(s => s.is_locked !== false).length;
    if (lockedCount === 0 && students.length > 0) {
      toast.info("All student records are already unlocked.");
      return;
    }
    const msg = `Are you sure you want to UNLOCK ALL ${students.length} enrolled students?\n\nThis will allow each student to re-enter and update their SML Rank & Category Rank ONCE on their next login or refresh. Upon saving, their profile will be permanently locked again.`;
    if (!window.confirm(msg)) return;

    setUnlockingAll(true);
    try {
      const res = await api.post("/medical-predictor/admin/guest-students/unlock-all");
      toast.success(res.data?.message || "Successfully unlocked all students!");
      fetchStudents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to unlock all students.");
    } finally {
      setUnlockingAll(false);
    }
  };

  // Toggle Single Student Lock / Unlock
  const handleToggleLock = async (student) => {
    const isCurrentlyLocked = student.is_locked !== false;
    const action = isCurrentlyLocked ? "unlock" : "lock";
    const confirmMsg = isCurrentlyLocked
      ? `Are you sure you want to UNLOCK ${student.candidate_name} (${student.reg_no})?\n\nThey will be able to edit their SML Rank & Category Rank once upon their next access.`
      : `Are you sure you want to LOCK ${student.candidate_name} (${student.reg_no})?\n\nThey will be restricted from editing their SML Rank.`;

    if (!window.confirm(confirmMsg)) return;

    try {
      const payload = {
        reg_no: student.reg_no,
        sml: student.sml,
        category: student.category,
        category_rank: student.category_rank,
        gender: student.gender,
        is_locked: !isCurrentlyLocked,
      };
      await api.put("/medical-predictor/admin/guest-students", payload);
      toast.success(`Successfully ${action}ed ${student.candidate_name}!`);
      fetchStudents();
    } catch (err) {
      toast.error(`Failed to ${action} student record.`);
    }
  };

  // Delete Single Student Record
  const handleDeleteStudent = async (student) => {
    const confirmMsg = `⚠️ DANGER: Are you sure you want to PERMANENTLY DELETE ${student.candidate_name} (${student.reg_no})?\n\nThis will delete their locked SML record from the system.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      await api.delete(`/medical-predictor/admin/guest-students/${encodeURIComponent(student.reg_no)}`);
      toast.success(`Deleted record for ${student.candidate_name}`);
      fetchStudents();
    } catch (err) {
      toast.error("Failed to delete student record.");
    }
  };

  // Open Edit Modal
  const handleOpenEdit = (student) => {
    setEditingStudent(student);
    setEditForm({
      reg_no: student.reg_no,
      sml: student.sml || "",
      category: student.category || "OPEN",
      category_rank: student.category_rank || "",
      gender: student.gender || "Male",
      is_locked: student.is_locked !== false,
    });
    setEditModalOpen(true);
  };

  // Save Edit Form
  const handleSaveEdit = async (e) => {
    e?.preventDefault();
    if (!editForm.sml || isNaN(parseInt(editForm.sml, 10)) || parseInt(editForm.sml, 10) <= 0) {
      toast.error("Please enter a valid SML rank.");
      return;
    }
    if (editForm.category !== "OPEN") {
      if (!editForm.category_rank || isNaN(parseInt(editForm.category_rank, 10)) || parseInt(editForm.category_rank, 10) <= 0) {
        toast.error(`Please enter a valid Category Rank for ${editForm.category}.`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        reg_no: editForm.reg_no,
        sml: parseInt(editForm.sml, 10),
        category: editForm.category,
        category_rank: editForm.category !== "OPEN" ? parseInt(editForm.category_rank, 10) : null,
        gender: editForm.gender,
        is_locked: editForm.is_locked,
      };
      const res = await api.put("/medical-predictor/admin/guest-students", payload);
      toast.success(res.data?.message || "Updated student record!");
      setEditModalOpen(false);
      fetchStudents();
      if (selectedStudent && selectedStudent.reg_no === editForm.reg_no) {
        setSelectedStudent(prev => ({ ...prev, ...payload }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update student.");
    } finally {
      setSaving(false);
    }
  };

  // Export CSV of Students
  const handleExportCsv = () => {
    if (!students.length) {
      toast.error("No student records to export.");
      return;
    }
    const headers = ["Reg No", "Candidate Name", "AIR", "SML", "Category", "Category Rank", "Gender", "Phone", "Status", "Locked Date"];
    const rows = filteredStudents.map(s => [
      `"${s.reg_no || ""}"`,
      `"${s.candidate_name || ""}"`,
      s.air || "",
      s.sml || "",
      s.category || "OPEN",
      s.category_rank || "",
      s.gender || "Male",
      `"${s.phone_number || ""}"`,
      s.is_locked !== false ? "Locked" : "Unlocked",
      `"${s.locked_at ? new Date(s.locked_at).toLocaleString("en-IN") : ""}"`
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `AME_Enrolled_Students_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchStudents();
    fetchPreferences();
  }, []);

  // Filtered Students List
  const filteredStudents = useMemo(() => {
    return students.filter(st => {
      const s = search.toLowerCase().trim();
      const matchSearch =
        !s ||
        (st.reg_no || "").toLowerCase().includes(s) ||
        (st.candidate_name || "").toLowerCase().includes(s) ||
        String(st.air || "").includes(s) ||
        String(st.sml || "").includes(s) ||
        (st.phone_number || "").includes(s);

      const matchCategory =
        categoryFilter === "ALL" || (st.category || "OPEN").toUpperCase() === categoryFilter.toUpperCase();

      const matchLock =
        lockFilter === "ALL" ||
        (lockFilter === "LOCKED" && st.is_locked !== false) ||
        (lockFilter === "UNLOCKED" && st.is_locked === false);

      return matchSearch && matchCategory && matchLock;
    });
  }, [students, search, categoryFilter, lockFilter]);

  // Filtered Preferences List
  const filteredPreferences = useMemo(() => {
    return preferencesList.filter(p => {
      const s = prefSearch.toLowerCase().trim();
      const matchSearch =
        !s ||
        (p.reg_no || "").toLowerCase().includes(s) ||
        (p.student_name || "").toLowerCase().includes(s);

      const matchCourse =
        prefCourseFilter === "ALL" || (p.course || "").toUpperCase() === prefCourseFilter.toUpperCase();

      return matchSearch && matchCourse;
    });
  }, [preferencesList, prefSearch, prefCourseFilter]);

  const categories = ["ALL", "OPEN", "EWS", "OBC", "SC", "ST", "VJ/NT", "SEBC"];

  // Match current viewingPref with student profile
  const viewingStudentProfile = useMemo(() => {
    if (!viewingPref) return null;
    return students.find(s => s.reg_no === viewingPref.reg_no) || {
      candidate_name: viewingPref.student_name || "Student",
      reg_no: viewingPref.reg_no,
      air: null,
      sml: null,
      category: "OPEN",
      category_rank: null,
      gender: "Male"
    };
  }, [viewingPref, students]);

  // Filtered modal preferences for workspace
  const filteredWorkspacePrefs = useMemo(() => {
    return modalPrefs.filter(p => {
      const s = workspaceSearch.toLowerCase().trim();
      const stat = (p.status || "").toLowerCase();
      const matchSearch = !s ||
        (p.college_name || "").toLowerCase().includes(s) ||
        (p.college_code || "").toLowerCase().includes(s);

      const matchType =
        workspaceTypeFilter === "ALL" ||
        (workspaceTypeFilter === "GOVT" && (stat.includes("govt") || stat.includes("corp"))) ||
        (workspaceTypeFilter === "PRIVATE" && !stat.includes("govt") && !stat.includes("corp"));

      return matchSearch && matchType;
    });
  }, [modalPrefs, workspaceSearch, workspaceTypeFilter]);

  // ═══════════════════════════════════════════════════════════
  // FULL-SCREEN PRIORITY EDITOR WORKSPACE (Instead of modal)
  // ═══════════════════════════════════════════════════════════
  if (viewingPref) {
    const govtCount = modalPrefs.filter(p => (p.status || "").toLowerCase().includes("govt") || (p.status || "").toLowerCase().includes("corp")).length;
    const privateCount = modalPrefs.filter(p => !(p.status || "").toLowerCase().includes("govt") && !(p.status || "").toLowerCase().includes("corp")).length;

    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#060913", color: "#f8fafc", fontFamily: "'Inter', sans-serif" }}>
        
        {/* Top Sticky Header */}
        <div style={{
          position: "sticky", top: 0, zIndex: 120,
          background: "rgba(6, 9, 19, 0.96)", backdropFilter: "blur(16px)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.09)", padding: "0.75rem 1.5rem"
        }}>
          <div style={{ maxWidth: 1300, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            
            {/* Back Button + Title */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                onClick={() => {
                  if (hasUnsavedChanges && !window.confirm("You have unsaved changes in this priority list. Are you sure you want to exit?")) return;
                  setViewingPref(null);
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(255, 255, 255, 0.12)",
                  color: "#cbd5e1", padding: "7px 14px", borderRadius: 8,
                  fontSize: "0.76rem", fontWeight: 700, cursor: "pointer", transition: "all 0.15s"
                }}
              >
                <ArrowLeft size={14} /> Back to Student Lists
              </button>

              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h1 style={{ margin: 0, fontSize: "1.12rem", fontWeight: 800, color: "#f8fafc" }}>
                    {viewingPref.student_name || "Student"}'s Priority List
                  </h1>
                  <span style={{ fontSize: "0.7rem", fontFamily: "monospace", color: "#fbbf24", background: "rgba(245, 158, 11, 0.15)", padding: "2px 8px", borderRadius: 4, fontWeight: 700 }}>
                    {viewingPref.reg_no}
                  </span>
                  <span style={{ fontSize: "0.7rem", color: "#c4b5fd", background: "rgba(139, 92, 246, 0.2)", padding: "2px 8px", borderRadius: 4, fontWeight: 800 }}>
                    {viewingPref.course || "MBBS"}
                  </span>
                </div>
                <div style={{ fontSize: "0.68rem", color: "#64748b", marginTop: 2 }}>
                  Admin Priority Workspace · Customize choice ordering, save directly to database, and download PDF.
                </div>
              </div>
            </div>

            {/* Quick Actions in Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {hasUnsavedChanges && (
                <span style={{ fontSize: "0.7rem", color: "#fbbf24", background: "rgba(245,158,11,0.1)", padding: "4px 10px", borderRadius: 20, border: "1px solid rgba(245,158,11,0.3)", display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 700 }}>
                  ● Unsaved Changes
                </span>
              )}

              <button
                onClick={handleAdminDownloadPdf}
                disabled={modalPrefs.length === 0}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(139,92,246,0.5)",
                  background: "linear-gradient(135deg, rgba(139,92,246,0.25), rgba(109,40,217,0.25))",
                  color: "#e9d5ff", fontSize: "0.78rem", fontWeight: 800, cursor: "pointer",
                }}
              >
                <Download size={14} /> Download Priority PDF
              </button>

              <button
                onClick={handleSaveModalPrefs}
                disabled={savingModalPrefs || modalPrefs.length === 0}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "8px 18px", borderRadius: 8, border: "none",
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "#fff", fontSize: "0.78rem", fontWeight: 800, cursor: savingModalPrefs ? "not-allowed" : "pointer",
                  boxShadow: "0 0 20px rgba(16,185,129,0.3)"
                }}
              >
                <Save size={14} /> {savingModalPrefs ? "Saving..." : "Save Order"}
              </button>
            </div>
          </div>
        </div>

        {/* Main Workspace Body */}
        <div style={{ maxWidth: 1300, margin: "0 auto", padding: "1.25rem 1.5rem 5rem" }}>
          
          {/* Student Profile Info Card & Summary Pills */}
          <div style={{
            background: "rgba(255, 255, 255, 0.025)", border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: 14, padding: "1rem 1.25rem", marginBottom: "1rem",
            display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12
          }}>
            {/* Student Info Tags */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{
                width: 34, height: 34, borderRadius: "50%",
                background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 800, fontSize: "0.85rem", color: "#fff"
              }}>
                {viewingStudentProfile?.candidate_name?.[0] || "S"}
              </div>

              <div>
                <div style={{ fontSize: "0.88rem", fontWeight: 800, color: "#f8fafc" }}>
                  {viewingStudentProfile?.candidate_name}
                </div>
                <div style={{ fontSize: "0.68rem", color: "#64748b" }}>
                  Reg: <span style={{ color: "#fbbf24" }}>{viewingStudentProfile?.reg_no}</span>
                </div>
              </div>

              <div style={{ height: 24, width: 1, background: "rgba(255,255,255,0.1)", margin: "0 4px" }} />

              {[
                viewingStudentProfile?.sml && { label: "SML", val: viewingStudentProfile.sml.toLocaleString("en-IN"), color: "#fbbf24" },
                viewingStudentProfile?.category_rank && { label: "Cat Rank", val: viewingStudentProfile.category_rank.toLocaleString("en-IN"), color: "#f59e0b" },
                viewingStudentProfile?.air && { label: "AIR", val: viewingStudentProfile.air.toLocaleString("en-IN"), color: "#38bdf8" },
                { label: "Category", val: viewingStudentProfile?.category || "OPEN", color: "#c4b5fd" },
                { label: "Gender", val: viewingStudentProfile?.gender || "Male", color: "#94a3b8" },
              ].filter(Boolean).map((t, idx) => (
                <span key={idx} style={{
                  fontSize: "0.68rem", fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                  color: t.color
                }}>
                  {t.label}: <strong style={{ color: "#f8fafc" }}>{t.val}</strong>
                </span>
              ))}
            </div>

            {/* List Summary Badges */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <div style={{ padding: "6px 12px", borderRadius: 8, background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.25)", textAlign: "center" }}>
                <span style={{ fontSize: "0.95rem", fontWeight: 900, color: "#c4b5fd" }}>{modalPrefs.length}</span>
                <span style={{ fontSize: "0.62rem", color: "#94a3b8", display: "block", fontWeight: 700, textTransform: "uppercase" }}>Total Choices</span>
              </div>
              <div style={{ padding: "6px 12px", borderRadius: 8, background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)", textAlign: "center" }}>
                <span style={{ fontSize: "0.95rem", fontWeight: 900, color: "#10b981" }}>{govtCount}</span>
                <span style={{ fontSize: "0.62rem", color: "#94a3b8", display: "block", fontWeight: 700, textTransform: "uppercase" }}>Govt / Corp</span>
              </div>
              <div style={{ padding: "6px 12px", borderRadius: 8, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)", textAlign: "center" }}>
                <span style={{ fontSize: "0.95rem", fontWeight: 900, color: "#f59e0b" }}>{privateCount}</span>
                <span style={{ fontSize: "0.62rem", color: "#94a3b8", display: "block", fontWeight: 700, textTransform: "uppercase" }}>Private</span>
              </div>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 10, marginBottom: "1rem"
          }}>
            <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 420 }}>
              <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#64748b" }} />
              <input
                type="text"
                value={workspaceSearch}
                onChange={e => setWorkspaceSearch(e.target.value)}
                placeholder="Search college name or choice code in list..."
                style={{
                  width: "100%", boxSizing: "border-box", padding: "8px 12px 8px 34px",
                  background: "rgba(255, 255, 255, 0.04)", border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: 8, color: "#f8fafc", fontSize: "0.78rem", outline: "none"
                }}
              />
            </div>

            <div style={{ display: "flex", gap: 6 }}>
              {["ALL", "GOVT", "PRIVATE"].map(t => (
                <button
                  key={t}
                  onClick={() => setWorkspaceTypeFilter(t)}
                  style={{
                    padding: "7px 14px", borderRadius: 8,
                    border: `1px solid ${workspaceTypeFilter === t ? "rgba(139,92,246,0.5)" : "rgba(255,255,255,0.08)"}`,
                    background: workspaceTypeFilter === t ? "rgba(139,92,246,0.2)" : "rgba(255,255,255,0.02)",
                    color: workspaceTypeFilter === t ? "#c4b5fd" : "#94a3b8",
                    fontSize: "0.74rem", fontWeight: 700, cursor: "pointer"
                  }}
                >
                  {t === "ALL" ? "All Types" : t}
                </button>
              ))}
            </div>
          </div>

          {/* Full-Width Interactive Priority Table */}
          <div style={{
            background: "rgba(255, 255, 255, 0.02)", border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: 14, overflow: "hidden", boxShadow: "0 10px 30px rgba(0,0,0,0.4)"
          }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem", textAlign: "left" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.09)", color: "#94a3b8" }}>
                    <th style={{ padding: "12px 8px", width: "5%", textAlign: "center" }}>Pref #</th>
                    <th style={{ padding: "12px 8px", width: "5%", textAlign: "center", color: "#38bdf8" }}>Ideal #</th>
                    <th style={{ padding: "12px 8px", width: "10%", textAlign: "center" }}>Reorder</th>
                    <th style={{ padding: "12px 10px", width: "8%" }}>Choice Code</th>
                    <th style={{ padding: "12px 10px", width: "26%" }}>College Name</th>
                    <th style={{ padding: "12px 8px", width: "7%" }}>Type</th>
                    <th style={{ padding: "12px 10px", width: "11%" }}>Annual Fee</th>
                    <th style={{ padding: "12px 8px", width: "8%", textAlign: "center" }}>Cutoff Tier</th>
                    <th style={{ padding: "12px 12px", width: "16%", textAlign: "right" }}>Your / Cutoff Rank</th>
                    <th style={{ padding: "12px 6px", width: "4%", textAlign: "center" }}>Del</th>
                  </tr>
                </thead>
                <tbody style={{ divideY: "1px solid rgba(255,255,255,0.04)" }}>
                  {filteredWorkspacePrefs.length === 0 ? (
                    <tr>
                      <td colSpan={10} style={{ padding: "3rem", textAlign: "center", color: "#64748b" }}>
                        No colleges found matching search criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredWorkspacePrefs.map((p, displayIdx) => {
                      const actualIdx = modalPrefs.findIndex(item => item.college_name === p.college_name && item.matched_category === p.matched_category);
                      const isGovt = (p.status || "").toLowerCase().includes("govt");
                      const isCorp = (p.status || "").toLowerCase().includes("corp");
                      const typeLabel = isGovt ? "GOVT" : isCorp ? "CORP" : "PRIVATE";
                      const typeColor = isGovt ? "#10b981" : isCorp ? "#38bdf8" : "#f59e0b";
                      const isNew = !!p.is_new_college || p.cutoff_rank == null;

                      return (
                        <tr
                          key={`${p.college_name}-${actualIdx}`}
                          style={{
                            borderBottom: "1px solid rgba(255,255,255,0.04)",
                            background: displayIdx % 2 === 0 ? "rgba(255,255,255,0.01)" : "transparent",
                            transition: "background 0.15s"
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                          onMouseLeave={e => e.currentTarget.style.background = displayIdx % 2 === 0 ? "rgba(255,255,255,0.01)" : "transparent"}
                        >
                          {/* Pref Rank Badge */}
                          <td style={{ padding: "10px", textAlign: "center" }}>
                            <span style={{
                              display: "inline-block", minWidth: 32, padding: "3px 6px", borderRadius: 6,
                              background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.35)",
                              color: "#fbbf24", fontWeight: 900, fontSize: "0.86rem"
                            }}>
                              #{actualIdx + 1}
                            </span>
                          </td>

                          {/* Ideal Order Badge */}
                          <td style={{ padding: "10px", textAlign: "center" }}>
                            <span style={{
                              display: "inline-block", minWidth: 30, padding: "2px 6px", borderRadius: 6,
                              background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.25)",
                              color: "#38bdf8", fontWeight: 800, fontSize: "0.78rem"
                            }}>
                              {p.priority_order != null ? `#${p.priority_order}` : "—"}
                            </span>
                          </td>

                          {/* Interactive Move Controls */}
                          <td style={{ padding: "8px 10px", textAlign: "center" }}>
                            <div style={{ display: "inline-flex", gap: 3, background: "rgba(0,0,0,0.25)", padding: "3px 5px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.06)" }}>
                              <button
                                onClick={() => moveModalPrefToTop(actualIdx)}
                                disabled={actualIdx === 0}
                                title="Jump to Top (#1)"
                                style={{
                                  padding: "3px 5px", borderRadius: 4, border: "none",
                                  background: actualIdx === 0 ? "transparent" : "rgba(139,92,246,0.15)",
                                  color: actualIdx === 0 ? "#334155" : "#c4b5fd",
                                  cursor: actualIdx === 0 ? "not-allowed" : "pointer"
                                }}
                              >
                                <ChevronsUp size={13} />
                              </button>

                              <button
                                onClick={() => moveModalPrefUp(actualIdx)}
                                disabled={actualIdx === 0}
                                title="Move Up 1 Position"
                                style={{
                                  padding: "3px 6px", borderRadius: 4, border: "none",
                                  background: actualIdx === 0 ? "transparent" : "rgba(255,255,255,0.08)",
                                  color: actualIdx === 0 ? "#334155" : "#38bdf8",
                                  cursor: actualIdx === 0 ? "not-allowed" : "pointer"
                                }}
                              >
                                <ArrowUp size={13} />
                              </button>

                              <button
                                onClick={() => moveModalPrefDown(actualIdx)}
                                disabled={actualIdx === modalPrefs.length - 1}
                                title="Move Down 1 Position"
                                style={{
                                  padding: "3px 6px", borderRadius: 4, border: "none",
                                  background: actualIdx === modalPrefs.length - 1 ? "transparent" : "rgba(255,255,255,0.08)",
                                  color: actualIdx === modalPrefs.length - 1 ? "#334155" : "#38bdf8",
                                  cursor: actualIdx === modalPrefs.length - 1 ? "not-allowed" : "pointer"
                                }}
                              >
                                <ArrowDown size={13} />
                              </button>

                              <button
                                onClick={() => moveModalPrefToBottom(actualIdx)}
                                disabled={actualIdx === modalPrefs.length - 1}
                                title="Jump to Bottom"
                                style={{
                                  padding: "3px 5px", borderRadius: 4, border: "none",
                                  background: actualIdx === modalPrefs.length - 1 ? "transparent" : "rgba(139,92,246,0.15)",
                                  color: actualIdx === modalPrefs.length - 1 ? "#334155" : "#c4b5fd",
                                  cursor: actualIdx === modalPrefs.length - 1 ? "not-allowed" : "pointer"
                                }}
                              >
                                <ChevronsDown size={13} />
                              </button>
                            </div>
                          </td>

                          {/* Choice Code */}
                          <td style={{ padding: "10px 12px", fontFamily: "monospace", color: "#38bdf8", fontWeight: 800, fontSize: "0.82rem" }}>
                            {p.college_code || "—"}
                          </td>

                          {/* College Name & Tags */}
                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ fontWeight: 700, color: "#f8fafc", lineHeight: 1.35 }}>
                              {p.college_name}
                            </div>
                            {isNew && (
                              <span style={{ fontSize: "0.6rem", color: "#f59e0b", fontWeight: 800, marginTop: 2, display: "inline-block" }}>
                                🆕 NEW COLLEGE (2026-27)
                              </span>
                            )}
                          </td>

                          {/* Type */}
                          <td style={{ padding: "10px" }}>
                            <span style={{
                              fontSize: "0.65rem", padding: "2px 7px", borderRadius: 4, fontWeight: 800,
                              background: isGovt ? "rgba(16,185,129,0.12)" : isCorp ? "rgba(56,189,248,0.12)" : "rgba(245,158,11,0.12)",
                              border: `1px solid ${isGovt ? "rgba(16,185,129,0.3)" : isCorp ? "rgba(56,189,248,0.3)" : "rgba(245,158,11,0.3)"}`,
                              color: typeColor
                            }}>
                              {typeLabel}
                            </span>
                          </td>

                          {/* Annual Fee */}
                          <td style={{ padding: "10px 12px" }}>
                            {p.fees && p.fees !== "—" ? (
                              <div>
                                <div style={{ fontSize: "0.8rem", color: "#c084fc", fontWeight: 800 }}>
                                  {String(p.fees).startsWith("₹") ? p.fees : `₹ ${p.fees}`}
                                </div>
                                <div style={{ fontSize: "0.6rem", color: "#64748b" }}>*Prev. Year · Check 2026-27</div>
                              </div>
                            ) : (
                              <span style={{ color: "#64748b" }}>—</span>
                            )}
                          </td>

                          {/* Cutoff Tier Badge */}
                          <td style={{ padding: "10px 8px", textAlign: "center" }}>
                            {(() => {
                              const tier = isNew ? null : (p.custom_tier || (p.margin_percent != null ? (p.margin_percent < -8 ? "Dream" : p.margin_percent <= 8 ? "Target" : p.margin_percent <= 25 ? "Possible" : "Safe") : "Dream"));
                              const tierStyles = {
                                Safe:     { bg: "rgba(16,185,129,0.15)",  border: "rgba(16,185,129,0.4)",  text: "#10b981" },
                                Possible: { bg: "rgba(59,130,246,0.15)",  border: "rgba(59,130,246,0.4)",  text: "#3b82f6" },
                                Target:   { bg: "rgba(245,158,11,0.15)",  border: "rgba(245,158,11,0.4)",  text: "#f59e0b" },
                                Dream:    { bg: "rgba(244,63,94,0.15)",   border: "rgba(244,63,94,0.4)",   text: "#f43f5e" },
                              };
                              const ts = tier ? (tierStyles[tier] || tierStyles.Dream) : null;
                              return isNew ? (
                                <span style={{ fontSize: "0.62rem", padding: "2px 7px", borderRadius: 4, fontWeight: 800, background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.35)", color: "#f59e0b" }}>
                                  NEW
                                </span>
                              ) : (
                                <span style={{ fontSize: "0.62rem", padding: "2px 7px", borderRadius: 4, fontWeight: 800, background: ts.bg, border: `1px solid ${ts.border}`, color: ts.text, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                                  {tier}
                                </span>
                              );
                            })()}
                          </td>

                          {/* Your / Cutoff Rank Breakdown */}
                          <td style={{ padding: "8px 12px", textAlign: "right" }}>
                            {(() => {
                              const smlMatch = p.all_matches?.["SML"];
                              const catMatch = p.all_matches?.["CAT-RANK"];
                              const hasBoth  = smlMatch && catMatch;
                              const bestType = p.matched_rank_type || "SML";

                              if (isNew) {
                                return (
                                  <div>
                                    <span style={{ fontSize: "0.55rem", color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.6px", fontWeight: 800, display: "block" }}>
                                      NEW SEATS
                                    </span>
                                    <span style={{ fontSize: "0.65rem", color: "#64748b" }}>Fresh Seat Pool</span>
                                  </div>
                                );
                              }

                              if (hasBoth) {
                                return (
                                  <div>
                                    <div style={{ fontSize: "0.54rem", color: "#fbbf24", textTransform: "uppercase", letterSpacing: "0.6px", fontWeight: 800, marginBottom: 2, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3 }}>
                                      ⭐ BEST: {bestType === "CAT-RANK" ? (catMatch.matched_category || "CAT-RANK") : "SML"}
                                    </div>
                                    <div style={{ fontSize: "0.68rem", color: bestType === "CAT-RANK" ? "#f1f5f9" : "#94a3b8", fontWeight: bestType === "CAT-RANK" ? 800 : 600, lineHeight: 1.3 }}>
                                      <span style={{ fontSize: "0.55rem", color: bestType === "CAT-RANK" ? "#f59e0b" : "#64748b", fontWeight: 700, marginRight: 3 }}>
                                        {catMatch.matched_category || "CAT"}:
                                      </span>
                                      <span>{catMatch.student_rank?.toLocaleString("en-IN")}</span>
                                      <span style={{ color: "#475569", margin: "0 2px" }}>/</span>
                                      <span>{catMatch.cutoff_rank?.toLocaleString("en-IN")}</span>
                                      {catMatch.cutoff_rank && (
                                        <span style={{ fontSize: "0.6rem", fontWeight: 800, color: (catMatch.student_rank - catMatch.cutoff_rank <= 0) ? "#10b981" : "#ef4444", marginLeft: 3 }}>
                                          ({catMatch.student_rank - catMatch.cutoff_rank <= 0 ? "-" : "+"}{Math.abs(Math.round(catMatch.student_rank - catMatch.cutoff_rank)).toLocaleString("en-IN")})
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ fontSize: "0.68rem", color: bestType === "SML" ? "#f1f5f9" : "#64748b", fontWeight: bestType === "SML" ? 800 : 500, lineHeight: 1.3, marginTop: 1 }}>
                                      <span style={{ fontSize: "0.55rem", color: bestType === "SML" ? "#38bdf8" : "#475569", fontWeight: 700, marginRight: 3 }}>
                                        SML:
                                      </span>
                                      <span>{smlMatch.student_rank?.toLocaleString("en-IN")}</span>
                                      <span style={{ color: "#334155", margin: "0 2px" }}>/</span>
                                      <span>{smlMatch.cutoff_rank?.toLocaleString("en-IN")}</span>
                                      {smlMatch.cutoff_rank && (
                                        <span style={{ fontSize: "0.6rem", fontWeight: 700, color: (smlMatch.student_rank - smlMatch.cutoff_rank <= 0) ? "#10b981" : "#ef4444", marginLeft: 3 }}>
                                          ({smlMatch.student_rank - smlMatch.cutoff_rank <= 0 ? "-" : "+"}{Math.abs(Math.round(smlMatch.student_rank - smlMatch.cutoff_rank)).toLocaleString("en-IN")})
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div>
                                  <div style={{ fontSize: "0.52rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.6px", fontWeight: 700 }}>
                                    {p.matched_rank_type || "SML"} MATCH
                                  </div>
                                  <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#f1f5f9" }}>
                                    {p.student_rank ? p.student_rank.toLocaleString("en-IN") : "—"}
                                    <span style={{ color: "#475569", fontWeight: 400, margin: "0 3px" }}>/</span>
                                    {p.cutoff_rank  ? p.cutoff_rank.toLocaleString("en-IN")  : "—"}
                                  </div>
                                  {p.student_rank && p.cutoff_rank && (
                                    <div style={{ fontSize: "0.62rem", fontWeight: 800, color: (p.student_rank - p.cutoff_rank <= 0) ? "#10b981" : "#ef4444" }}>
                                      ({p.student_rank - p.cutoff_rank <= 0 ? "-" : "+"}{Math.abs(Math.round(p.student_rank - p.cutoff_rank)).toLocaleString("en-IN")})
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>

                          {/* Delete Action */}
                          <td style={{ padding: "10px 6px", textAlign: "center" }}>
                            <button
                              onClick={() => removeModalPref(actualIdx)}
                              style={{
                                padding: "4px 7px", borderRadius: 6, border: "none",
                                background: "rgba(239,68,68,0.1)", color: "#f87171", cursor: "pointer",
                                transition: "all 0.15s"
                              }}
                              title="Delete Choice"
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sticky Bottom Bar */}
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 110,
          background: "rgba(6, 9, 19, 0.96)", backdropFilter: "blur(14px)",
          borderTop: "1px solid rgba(255, 255, 255, 0.09)", padding: "0.75rem 1.5rem"
        }}>
          <div style={{ maxWidth: 1300, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div style={{ fontSize: "0.76rem", color: "#94a3b8" }}>
              Selected <strong style={{ color: "#f8fafc" }}>{modalPrefs.length} choices</strong> for {viewingPref.student_name} ({viewingPref.course || "MBBS"}).
              {hasUnsavedChanges && <span style={{ color: "#fbbf24", marginLeft: 6 }}>● Click Save Order to commit changes.</span>}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setViewingPref(null)}
                style={{
                  padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)",
                  background: "transparent", color: "#94a3b8", cursor: "pointer", fontWeight: 700, fontSize: "0.76rem"
                }}
              >
                Close Workspace
              </button>

              <button
                onClick={handleSaveModalPrefs}
                disabled={savingModalPrefs || modalPrefs.length === 0}
                style={{
                  padding: "8px 20px", borderRadius: 8, border: "none",
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "#fff", cursor: "pointer", fontWeight: 800, fontSize: "0.78rem",
                  boxShadow: "0 0 20px rgba(16,185,129,0.3)"
                }}
              >
                {savingModalPrefs ? "Saving..." : "Save Order"}
              </button>
            </div>
          </div>
        </div>

      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // STUDENT PREDICTOR VIEW
  // ═══════════════════════════════════════════════════════════
  if (selectedStudent) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#0a0f1e", color: "#f8fafc" }}>
        {/* Top Return Bar */}
        <div style={{
          background: "rgba(10, 15, 30, 0.96)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          padding: "0.65rem 1.25rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 110,
          backdropFilter: "blur(14px)"
        }}>
          <button
            onClick={() => setSelectedStudent(null)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2))",
              border: "1px solid rgba(245, 158, 11, 0.4)",
              color: "#fbbf24",
              padding: "6px 14px",
              borderRadius: 8,
              fontSize: "0.75rem",
              fontWeight: 800,
              cursor: "pointer",
              fontFamily: "inherit",
              transition: "all 0.2s"
            }}
          >
            <ArrowLeft size={14} /> Back to SML Registry
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.74rem", color: "#94a3b8" }}>Viewing Student Predictor:</span>
            <span style={{ fontSize: "0.82rem", fontWeight: 800, color: "#f8fafc" }}>{selectedStudent.candidate_name}</span>
            <span style={{ fontSize: "0.72rem", fontFamily: "monospace", color: "#f59e0b", background: "rgba(245, 158, 11, 0.1)", padding: "2px 8px", borderRadius: 4, border: "1px solid rgba(245, 158, 11, 0.2)" }}>
              {selectedStudent.reg_no}
            </span>
          </div>

          <button
            onClick={() => handleOpenEdit(selectedStudent)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              color: "#e2e8f0",
              padding: "6px 12px",
              borderRadius: 8,
              fontSize: "0.72rem",
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit"
            }}
          >
            <Edit2 size={13} /> Edit SML
          </button>
        </div>

        {/* Embedded Student Predictor Tool */}
        <VerifiedPredictor
          student={selectedStudent}
          isAdminMode={true}
          adminSavedStudents={students}
          onSwitchStudent={(s) => setSelectedStudent(s)}
          onUpdateStudent={(updated) => {
            setStudents(prev => prev.map(s => s.reg_no === updated.reg_no ? { ...s, ...updated } : s));
            setSelectedStudent(updated);
          }}
          onLogout={() => setSelectedStudent(null)}
        />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" style={{ minHeight: "100vh", backgroundColor: "#020617", color: "#f8fafc" }}>
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-amber-500" />
            <h1 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-100">
              Guest Predictor Admin Portal
            </h1>
          </div>
          <p className="text-xs md:text-sm text-slate-400 mt-1">
            Manage enrolled student SML locks, access predictor profiles, and view custom student priority preference lists.
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          {adminTab === "students" && (
            <button
              onClick={handleUnlockAll}
              disabled={unlockingAll || loading || students.length === 0}
              className="flex items-center gap-2 px-3.5 py-2 text-xs font-bold rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 shadow-lg shadow-amber-950/40 transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Unlock all students so they can edit SML and Category Rank once"
            >
              <Unlock className={`h-3.5 w-3.5 ${unlockingAll ? "animate-pulse" : ""}`} />
              {unlockingAll ? "Unlocking All..." : "Unlock All Students"}
            </button>
          )}
          <button
            onClick={() => { fetchStudents(); fetchPreferences(); }}
            disabled={loading || prefLoading}
            className="flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-lg border border-slate-700 bg-slate-800/80 hover:bg-slate-700 text-slate-200 transition"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading || prefLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {adminTab === "students" && (
            <button
              onClick={handleExportCsv}
              className="flex items-center gap-2 px-3.5 py-2 text-xs font-bold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/30 transition"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Top Level Tab Navigation */}
      <div className="flex items-center gap-2 border-b border-slate-800 pb-2">
        <button
          onClick={() => setAdminTab("students")}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs font-extrabold rounded-lg transition ${
            adminTab === "students"
              ? "bg-amber-500/15 border border-amber-500/40 text-amber-300 shadow-lg shadow-amber-950/30"
              : "bg-slate-900/60 border border-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-800"
          }`}
        >
          <ShieldCheck className="h-4 w-4 text-amber-500" />
          Enrolled SML Registry
          <span className="ml-1.5 px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-500/20 text-amber-300">
            {students.length}
          </span>
        </button>

        <button
          onClick={() => setAdminTab("preferences")}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs font-extrabold rounded-lg transition ${
            adminTab === "preferences"
              ? "bg-purple-500/15 border border-purple-500/40 text-purple-300 shadow-lg shadow-purple-950/30"
              : "bg-slate-900/60 border border-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-800"
          }`}
        >
          <ListOrdered className="h-4 w-4 text-purple-400" />
          Student Priority Lists
          <span className="ml-1.5 px-2 py-0.5 rounded-full text-[10px] font-black bg-purple-500/20 text-purple-300">
            {preferencesList.length}
          </span>
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════
          TAB 1: ENROLLED SML REGISTRY
      ═══════════════════════════════════════════════════════ */}
      {adminTab === "students" && (
        <>
          {/* Metric Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/60 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Registered</div>
                <div className="text-xl font-black text-slate-100 mt-0.5">{students.length}</div>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/60 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                <Lock className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Locked</div>
                <div className="text-xl font-black text-emerald-400 mt-0.5">
                  {students.filter(s => s.is_locked !== false).length}
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/60 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-400">
                <Award className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Categories</div>
                <div className="text-xl font-black text-sky-400 mt-0.5">
                  {students.filter(s => (s.category || "OPEN").toUpperCase() !== "OPEN").length}
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/60 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400">
                <Unlock className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Unlocked</div>
                <div className="text-xl font-black text-rose-400 mt-0.5">
                  {students.filter(s => s.is_locked === false).length}
                </div>
              </div>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/60 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by Reg No, Student Name, AIR, SML, or Phone..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-xs md:text-sm rounded-lg bg-slate-950/80 border border-slate-800 focus:border-amber-500 focus:outline-none text-slate-100 placeholder-slate-500"
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="px-3 py-2 text-xs font-bold rounded-lg bg-slate-950/80 border border-slate-800 text-slate-300 focus:border-amber-500 focus:outline-none"
              >
                {categories.map(c => <option key={c} value={c}>{c === "ALL" ? "All Categories" : c}</option>)}
              </select>

              <select
                value={lockFilter}
                onChange={e => setLockFilter(e.target.value)}
                className="px-3 py-2 text-xs font-bold rounded-lg bg-slate-950/80 border border-slate-800 text-slate-300 focus:border-amber-500 focus:outline-none"
              >
                <option value="ALL">All Lock Status</option>
                <option value="LOCKED">Locked</option>
                <option value="UNLOCKED">Unlocked</option>
              </select>
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-950/80 text-slate-400 font-bold border-b border-slate-800">
                  <tr>
                    <th className="p-3">Student Name</th>
                    <th className="p-3">Reg No</th>
                    <th className="p-3">AIR</th>
                    <th className="p-3">SML Rank</th>
                    <th className="p-3">Category</th>
                    <th className="p-3">Gender</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-500">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto text-amber-500 mb-2" />
                        Loading student records...
                      </td>
                    </tr>
                  ) : filteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-500">
                        No student records found matching filters.
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map(st => {
                      const isLocked = st.is_locked !== false;
                      return (
                        <tr key={st.reg_no} className="hover:bg-slate-800/40 transition">
                          <td className="p-3 font-extrabold text-slate-100">
                            <button
                              onClick={() => setSelectedStudent(st)}
                              className="text-left hover:text-amber-400 flex items-center gap-1.5 transition"
                            >
                              <span>{st.candidate_name}</span>
                              <Eye className="h-3 w-3 text-amber-400/70 opacity-0 group-hover:opacity-100" />
                            </button>
                            {st.phone_number && (
                              <div className="text-[11px] text-slate-400 font-normal mt-0.5 flex items-center gap-1">
                                <Phone className="h-2.5 w-2.5 text-slate-500" /> {st.phone_number}
                              </div>
                            )}
                          </td>
                          <td className="p-3 font-mono font-bold text-amber-400">{st.reg_no}</td>
                          <td className="p-3 font-bold text-slate-200">{st.air ? st.air.toLocaleString("en-IN") : "—"}</td>
                          <td className="p-3 font-black text-amber-300">{st.sml ? st.sml.toLocaleString("en-IN") : "—"}</td>
                          <td className="p-3">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-slate-200 border border-slate-700">
                              {st.category || "OPEN"}
                              {st.category_rank ? ` (#${st.category_rank})` : ""}
                            </span>
                          </td>
                          <td className="p-3 text-slate-300 font-medium">{st.gender || "Male"}</td>
                          <td className="p-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black ${
                              isLocked
                                ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                                : "bg-rose-500/10 text-rose-400 border border-rose-500/30 animate-pulse"
                            }`}>
                              {isLocked ? <Lock className="h-2.5 w-2.5" /> : <Unlock className="h-2.5 w-2.5" />}
                              {isLocked ? "LOCKED" : "UNLOCKED"}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => setSelectedStudent(st)}
                                className="px-2.5 py-1 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 text-[11px] font-bold transition flex items-center gap-1"
                                title="Open Student Predictor Dashboard"
                              >
                                <Eye className="h-3 w-3" /> View
                              </button>
                              <button
                                onClick={() => handleOpenEdit(st)}
                                className="p-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition"
                                title="Edit SML Rank"
                              >
                                <Edit2 className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleToggleLock(st)}
                                className={`p-1.5 rounded border transition ${
                                  isLocked
                                    ? "bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border-rose-500/30"
                                    : "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                                }`}
                                title={isLocked ? "Unlock student profile" : "Lock student profile"}
                              >
                                {isLocked ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                              </button>
                              <button
                                onClick={() => handleDeleteStudent(st)}
                                className="p-1.5 rounded bg-rose-950/40 hover:bg-rose-900/60 text-rose-400 border border-rose-800/40 transition"
                                title="Permanently delete record"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════
          TAB 2: STUDENT PRIORITY LISTS VIEW
      ═══════════════════════════════════════════════════════ */}
      {adminTab === "preferences" && (
        <div className="space-y-4">
          {/* Priority Lists Metric Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/60 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/30 text-purple-400">
                <ListOrdered className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Lists Saved</div>
                <div className="text-xl font-black text-purple-300 mt-0.5">{preferencesList.length}</div>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/60 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400">
                <Award className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">MBBS Lists</div>
                <div className="text-xl font-black text-amber-300 mt-0.5">
                  {preferencesList.filter(p => (p.course || "MBBS").toUpperCase() === "MBBS").length}
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/60 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-400">
                <BookOpen className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">BDS Lists</div>
                <div className="text-xl font-black text-sky-300 mt-0.5">
                  {preferencesList.filter(p => (p.course || "").toUpperCase() === "BDS").length}
                </div>
              </div>
            </div>
          </div>

          {/* Search & Course Filter */}
          <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/60 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by student name or Reg No..."
                value={prefSearch}
                onChange={e => setPrefSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-xs md:text-sm rounded-lg bg-slate-950/80 border border-slate-800 focus:border-purple-500 focus:outline-none text-slate-100 placeholder-slate-500"
              />
            </div>

            <div className="flex gap-2">
              {["ALL", "MBBS", "BDS"].map(c => (
                <button
                  key={c}
                  onClick={() => setPrefCourseFilter(c)}
                  className={`px-3 py-2 text-xs font-bold rounded-lg transition ${
                    prefCourseFilter === c
                      ? "bg-purple-600 text-white shadow-lg shadow-purple-950/40"
                      : "bg-slate-950/80 border border-slate-800 text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {c === "ALL" ? "All Courses" : c}
                </button>
              ))}
            </div>
          </div>

          {/* Priority Lists Table */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-950/80 text-slate-400 font-bold border-b border-slate-800">
                  <tr>
                    <th className="p-3">Student Name</th>
                    <th className="p-3">Reg No</th>
                    <th className="p-3">Course</th>
                    <th className="p-3 text-center">Choices Saved</th>
                    <th className="p-3">Saved At</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {prefLoading ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-500">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto text-purple-500 mb-2" />
                        Loading priority lists...
                      </td>
                    </tr>
                  ) : filteredPreferences.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-500">
                        No saved priority lists found.
                      </td>
                    </tr>
                  ) : (
                    filteredPreferences.map((pref, idx) => (
                      <tr key={`${pref.reg_no}-${pref.course}-${idx}`} className="hover:bg-slate-800/40 transition">
                        <td className="p-3 font-extrabold text-slate-100 flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-300 flex items-center justify-center text-[10px] font-bold">
                            {pref.student_name?.[0] || "S"}
                          </div>
                          <span>{pref.student_name || "Enrolled Student"}</span>
                        </td>
                        <td className="p-3 font-mono font-bold text-amber-400">{pref.reg_no}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-black ${
                            pref.course === "BDS" ? "bg-sky-500/15 text-sky-300 border border-sky-500/30" : "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                          }`}>
                            {pref.course || "MBBS"}
                          </span>
                        </td>
                        <td className="p-3 text-center font-black text-purple-300 text-sm">
                          {pref.total_choices || pref.preferences?.length || 0}
                        </td>
                        <td className="p-3 text-slate-400 font-medium">
                          {pref.saved_at ? new Date(pref.saved_at).toLocaleString("en-IN") : "—"}
                        </td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => handleOpenViewingPref(pref)}
                            className="px-3.5 py-1.5 rounded-lg bg-gradient-to-r from-purple-600/30 to-purple-800/30 border border-purple-500/50 text-purple-200 hover:from-purple-600/40 hover:to-purple-800/40 text-xs font-extrabold transition inline-flex items-center gap-1.5 shadow-lg shadow-purple-950/30"
                          >
                            <Eye className="h-3.5 w-3.5" /> Open Priority Workspace
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Edit Student SML Modal */}
      {editModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16
        }}>
          <div style={{
            background: "#0f172a", border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 16, width: "100%", maxWidth: 440, padding: 24,
            boxShadow: "0 20px 40px rgba(0,0,0,0.5)"
          }}>
            <h3 style={{ margin: "0 0 16px", color: "#f8fafc", fontSize: "1.1rem", fontWeight: 700 }}>
              Edit Student SML: {editingStudent?.candidate_name}
            </h3>

            <form onSubmit={handleSaveEdit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: "0.72rem", color: "#94a3b8", marginBottom: 4, fontWeight: 700 }}>
                  State Merit List Rank (SML) *
                </label>
                <input
                  type="number"
                  value={editForm.sml}
                  onChange={e => setEditForm({ ...editForm, sml: e.target.value })}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", boxSizing: "border-box" }}
                  required
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ display: "block", fontSize: "0.72rem", color: "#94a3b8", marginBottom: 4, fontWeight: 700 }}>
                    Category *
                  </label>
                  <select
                    value={editForm.category}
                    onChange={e => setEditForm({ ...editForm, category: e.target.value, category_rank: e.target.value === "OPEN" ? "" : editForm.category_rank })}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", boxSizing: "border-box" }}
                  >
                    {categories.filter(c => c !== "ALL").map(c => <option key={c} value={c} style={{ background: "#0f172a" }}>{c}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "0.72rem", color: "#94a3b8", marginBottom: 4, fontWeight: 700 }}>
                    Gender *
                  </label>
                  <select
                    value={editForm.gender}
                    onChange={e => setEditForm({ ...editForm, gender: e.target.value })}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", boxSizing: "border-box" }}
                  >
                    <option value="Male" style={{ background: "#0f172a" }}>Male</option>
                    <option value="Female" style={{ background: "#0f172a" }}>Female</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: "0.72rem", color: editForm.category === "OPEN" ? "#475569" : "#94a3b8", marginBottom: 4, fontWeight: 700 }}>
                  Category Rank {editForm.category === "OPEN" ? "(Not Applicable)" : "*"}
                </label>
                <input
                  type="number"
                  disabled={editForm.category === "OPEN"}
                  value={editForm.category_rank}
                  onChange={e => setEditForm({ ...editForm, category_rank: e.target.value })}
                  style={{
                    width: "100%", padding: "8px 12px", borderRadius: 8,
                    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
                    color: "#f8fafc", boxSizing: "border-box", opacity: editForm.category === "OPEN" ? 0.4 : 1
                  }}
                />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                <input
                  type="checkbox"
                  id="lockCheckbox"
                  checked={editForm.is_locked}
                  onChange={e => setEditForm({ ...editForm, is_locked: e.target.checked })}
                  style={{ width: 16, height: 16, accentColor: "#f59e0b" }}
                />
                <label htmlFor="lockCheckbox" style={{ fontSize: "0.78rem", color: "#cbd5e1", cursor: "pointer", fontWeight: 600 }}>
                  Permanently Lock Profile (Prevent student from editing)
                </label>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => setEditModalOpen(false)}
                  style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "transparent", color: "#94a3b8", cursor: "pointer", fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", cursor: "pointer", fontWeight: 700 }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
